import React from "react";
import PageLayout from "../components/PageLayout";
import Spacer from "../components/Spacer";
import ExperienceCard from "../components/ExperienceCard";

const About = () => {
  return (
    <PageLayout>
      <div className="text-[1.18rem] lg:text-[1.05rem] xl:text-[1.18rem] font-lusitana">
        {/* Page Header */}
        <h1 className="text-[2.8rem] mb-[0.2rem] sm:mb-[0rem] sm:text-[3.62rem] xl:text-[4rem] font-bold font-garamond">Hi! I'm Aaron.</h1>

        <div className="flex flex-col sm:flex-row items-start sm:items-stretch leading-[1.55rem] lg:leading-[1.35rem] xl:leading-[1.55rem]">
          {/* Image */}
          <img
            src="/hat-pic.png"
            alt="Aaron Zhu"
            className="w-[9rem] mr-3 object-cover"
            style={{ maxHeight: "16rem" }} 
          />

          {/* Text */}
          <div className="mt-4 sm:mt-0">
            <p>
              I recently graduated from Columbia, where I studied computer science and linguistics. I like solving
              hard technical problems and designing intuitive interfaces—building software that is fast, reliable,
              and easy to use. I also love teaching and writing, about technology and just about everything else.
            </p>
          </div>
        </div>

        <Spacer height="0.8rem" />
        <div className="leading-[1.55rem] lg:leading-[1.35rem] xl:leading-[1.55rem]">
          I'm looking for an engineering role where I can tackle difficult problems. If you'd like to connect, shoot
          me an email at
          <a href="mailto:aaron.zhu@columbia.edu" className="ml-1 text-blue-800 underline">
             aaron.zhu@columbia.edu
          </a>.
        </div>
        <Spacer height="0.6rem" />

        <h1 className="text-[2.2rem] font-bold font-garamond">Where I've Worked</h1>
        <Spacer height="0.3rem" />

        {/* Experience Cards */}
        <div className="flex-col items-center justify-center space-y-[0.62rem]">
          <ExperienceCard
            logo="/company-logos/aws.jpeg"
            company="Amazon Web Services (AWS)"
            title="Software Development Engineer Intern"
            time="June 2025 - September 2025"
          />
          <ExperienceCard
            logo="/company-logos/certik.jpg"
            company="CertiK"
            title="Software Engineer Intern"
            time="June 2024 - August 2024"
          />
          <ExperienceCard
            logo="/company-logos/columbia_climate.png"
            company="Columbia Climate School"
            title="Software Developer"
            time="January 2024 - August 2024"
          />
          <ExperienceCard
            logo="/company-logos/columbia_med.jpg"
            company="Columbia Medical Center"
            title="Software Developer"
            time="January 2024 - August 2024"
          />
        </div>

        <Spacer height="1rem" />

        {/* Skills Section */}
        <h1 className="text-[2.2rem] font-bold font-garamond">My Skills</h1>
        <Spacer height="0.3rem" />

        {/* Sized to the grid's own width, not the viewport, since the window resizes independently */}
        <div className="grid grid-cols-3 gap-4 [container-type:inline-size]">
          {/* Languages Column */}
          <div className="bg-[#c0c0c0] p-[0.78rem] pt-1 border-[0.12rem] border-t-[#f5f5f5] border-l-[#f5f5f5] border-b-black border-r-black">
            <h2 className="text-left my-[0.4rem] font-bold text-[clamp(0.85rem,3.6cqw,1.4rem)] font-lusitana">
              Languages
            </h2>
            <ul className="space-y-1">
              {["Python", "JavaScript", "TypeScript", "C++", "C", "HTML", "CSS", "SQL", "Swift"].map((skill, index) => (
                <li
                  key={index}
                  className="experience-card-checker sm:px-2 py-1 text-center font-lusitana text-[clamp(0.8rem,3cqw,1.1rem)] border-[0.12rem] border-t-[#7f787e] border-l-[#7f787e] border-b-white border-r-white min-h-[2.5rem]"
                >
                  {skill}
                </li>
              ))}
            </ul>
          </div>

          {/* Frameworks Column */}
          <div className="bg-[#c0c0c0] p-[0.78rem] pt-1 border-[0.12rem] border-t-[#f5f5f5] border-l-[#f5f5f5] border-b-black border-r-black">
            <h2 className="text-left my-[0.4rem] font-bold text-[clamp(0.85rem,3.6cqw,1.4rem)] font-lusitana">
              Frameworks
            </h2>
            <ul className="space-y-1">
              {["React", "Tailwind", "Flask", "Express", "Node.js"].map((skill, index) => (
                <li
                  key={skill}
                  className="experience-card-checker sm:px-2 py-1 text-center font-lusitana text-[clamp(0.8rem,3cqw,1.1rem)] border-[0.12rem] border-t-[#7f787e] border-l-[#7f787e] border-b-white border-r-white min-h-[2.5rem]"
                >
                  {skill}
                </li>
              ))}
            </ul>
          </div>

          {/* Other Column */}
          <div className="bg-[#c0c0c0] p-[0.78rem] pt-1 border-[0.12rem] border-t-[#f5f5f5] border-l-[#f5f5f5] border-b-black border-r-black">
            <h2 className="text-left my-[0.4rem] font-bold text-[clamp(0.85rem,3.6cqw,1.4rem)] font-lusitana">
              Other
            </h2>
            <ul className="space-y-1">
              {["PostgreSQL", "Git", "Unix CLI", "AWS", "Figma", "UI Design"].map((skill, index) => (
                <li
                  key={index}
                  className="experience-card-checker sm:px-2 py-1 text-center font-lusitana text-[clamp(0.8rem,3cqw,1.1rem)] border-[0.12rem] border-t-[#7f787e] border-l-[#7f787e] border-b-white border-r-white min-h-[2.5rem]"
                >
                  {skill}
                </li>
              ))}
            </ul>
          </div>
        </div>

        <Spacer height="1.6rem" />

        {/* About Myself Section */}
        <h1 className="text-[2.2rem] font-bold font-garamond">A Bit About Myself</h1>
        <Spacer height="0.2rem" />
        <div className="border-[0rem] border-t-[#7f787e] border-l-[#7f787e] border-b-white border-r-white leading-[1.55rem]">
          I grew up in the lovely town of East Brunswick, New Jersey. As a kid, building was my sustenance. I spent hours making towns and castles in Minecraft, and even more on Roblox, where I picked up basic 3-D modeling and made my first foray into programming with the scripting language Lua. I also spent a lot of time outside and, for many years, sported a strange perennial tan.

          {/* Childhood Picture */}
          <div className="flex justify-center my-[1.2rem]">
            <img
              src="/childhood-pic.jpg"
              alt="Childhood Memory"
              className="w-[20rem] h-auto"
            />
          </div>
          I also wrote words, not just code. One of my favorite hobbies in elementary school was regurgitating facts from Wikipedia about zoo animals, the periodic table, and Minecraft biomes into booklets of loose-leaf. Yes—I was that kind of kid. As I grew older, I took on essays and the harder task of wrangling messy thoughts into clear prose. I read a lot and learned by imitating writers I admired.
          <Spacer height="0.8rem" />
          In high school, I became fascinated by foreign languages. I studied German for six years and am now learning Spanish. Each language has its own quirks and rhythms, and figuring them out comes with a precious reward: the ability to connect with millions of new people.
          <div className="flex justify-center my-[1.2rem]">
            <img
              src="/butler.jpg"
              alt="In the lib"
              className="w-[20rem] h-auto"
            />
          </div>
          In college, I studied computer science and linguistics because they brought these interests together. Code offered me a unique way to build, to give form to an idea—one line at a time. It was a familiar feeling, the same one that drew me to writing, teaching, and design: the pleasure of taking something complicated and making it feel simple, as though it couldn’t work any other way.
          <Spacer height="0.8rem" />
          If you’ve made it this far, you’re either unusually appreciative of long bios or trying to steal my identity. If it’s the latter, you should now have enough to work with. Either way, thanks for reading—and feel free to reach out!
          <img
            src="/square-sketch.png"
            alt="Aaron Zhu"
            className="mb-[0.3rem] ml-[0.3rem] h-[1rem] w-[1rem] inline-block"
          />
        </div>
        <Spacer height="1.35rem" />
      </div>
    </PageLayout>
  );
};

export default About;
